import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, PanelLeft, X } from 'lucide-react';
import { AppBackground, OfflineBanner } from '@shared/components';
import { cn } from '@core/utils/cn';
import { HOME_ROUTE } from '@config/routes';
import { useUiStore } from '@stores/ui.store';
import { useAssistantStore } from '@stores/assistant.store';
import { HistorySidebar } from '@features/assistant';
import { TopNav } from './TopNav';

const ICON_BUTTON =
  'flex size-9 items-center justify-center rounded-lg text-muted outline-none transition-colors ' +
  'hover:bg-card hover:text-text dda-focus';

/**
 * User-facing shell: top bar, history rail, routed content.
 *
 * The rail replaces the two cards that used to be the whole navigation. That was
 * the right call while the landing screen was the only way in — but a person with
 * fifteen projects needs to see them, not a card that counts them, and everything
 * the cards did (start something, go back to something) the rail does from every
 * route instead of only from home.
 *
 * Built with logical properties from the first commit rather than retrofitted:
 * a rail is the one piece of chrome that is wrong in every direction at once when
 * Arabic mirrors it.
 */
export function AppLayout(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const railCollapsed = useUiStore((state) => state.railCollapsed);
  const toggleRail = useUiStore((state) => state.toggleRail);
  const requestFocus = useAssistantStore((state) => state.requestFocus);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // A drawer left open across a navigation would cover the screen it navigated to.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        // The composer only exists on home, so ⌘K from a project page is a
        // navigation plus a focus request the composer answers once it mounts.
        void navigate(HOME_ROUTE);
        requestFocus();
        return;
      }
      if (event.key === 'Escape') setDrawerOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [navigate, requestFocus]);

  return (
    <div className="flex min-h-screen flex-col">
      <AppBackground />
      <OfflineBanner />

      <TopNav
        leading={
          <>
            <button
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
              aria-label={t('assistant.openRail')}
              aria-expanded={drawerOpen}
              className={cn(ICON_BUTTON, 'lg:hidden')}
            >
              <Menu size={18} aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={toggleRail}
              aria-label={t(railCollapsed ? 'assistant.showRail' : 'assistant.hideRail')}
              aria-pressed={!railCollapsed}
              className={cn(ICON_BUTTON, 'hidden lg:flex')}
            >
              <PanelLeft size={18} aria-hidden="true" className="rtl:rotate-180" />
            </button>
          </>
        }
      />

      <div className="flex min-h-0 flex-1">
        {!railCollapsed && (
          <aside className="dda-rail hidden w-72 shrink-0 lg:block">
            {/* Sticky under the 4rem bar so the rail scrolls its own list rather
                than the page. */}
            <div className="sticky top-16 h-[calc(100vh-4rem)]">
              <HistorySidebar />
            </div>
          </aside>
        )}

        <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
          <Outlet />
        </main>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={() => {
              setDrawerOpen(false);
            }}
            className="absolute inset-0 bg-black/50"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label={t('assistant.railLabel')}
            className="dda-rail dda-rail--drawer absolute inset-y-0 start-0 flex w-80 max-w-[85vw] flex-col"
          >
            <div className="flex h-14 shrink-0 items-center justify-end px-2">
              <button
                type="button"
                onClick={() => {
                  setDrawerOpen(false);
                }}
                aria-label={t('common.close')}
                className={ICON_BUTTON}
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <HistorySidebar
                onNavigate={() => {
                  setDrawerOpen(false);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
